import _ from 'lodash';
import { normalizeEmail } from './helpers';

function isString(value) {
  return typeof value === 'string';
}

function checkAtLeast8Characters(password) {
  if (!isString(password)) return false;
  return password.length >= 8;
}

export function checkNumber(password) {
  if (!isString(password)) return false;
  return /[0-9]/.test(password);
}

export function checkLowerCaseCharacter(password) {
  if (!isString(password)) return false;
  return /[a-z]/.test(password);
}

export function checkUpperCaseCharacter(password) {
  if (!isString(password)) return false;
  return /[A-Z]/.test(password);
}

export function checkSpecialCharacter(password) {
  if (!isString(password)) return false;
  return /[~`!@#$%^&*()_\-+={[}\]|\\:;"'<,>.?/]/.test(password);
}

export function checkIllegalCharacters(password) {
  if (!isString(password)) return false;
  // Allows ASCII characters, Latin-1 Supplement, and Latin Extended-A (which includes č, ť, etc.)
  return /^[\u0020-\u007E\u00A0-\u00FF\u0100-\u017F]+$/.test(password);
}

export function checkPasswordIsDifferentFromEmail(password, email) {
  if (!isString(password) || !email) return true;

  // Normalize values and convert to lower case
  const insensitiveEmail = email.trim().toLowerCase();
  const insensitivePassword = password.toLowerCase();
  const normalizedEmail = normalizeEmail(insensitiveEmail);
  const [localPart] = insensitiveEmail.split('@');
  const [normalizedLocalPart] = normalizedEmail.split('@');

  // Return true if password is not one of invalid values derived from email
  return ![insensitiveEmail, normalizedEmail, localPart, normalizedLocalPart].includes(insensitivePassword);
}

export function checkPasswordStrength(password, email) {
  // Strength indicators with weights
  const results = [
    { key: 'hasAtLeast8Characters', isValid: checkAtLeast8Characters(password), weight: 1 },
    { key: 'hasNumber', isValid: checkNumber(password), weight: 1 },
    { key: 'hasLowerCaseCharacter', isValid: checkLowerCaseCharacter(password), weight: 1 },
    { key: 'hasUpperCaseCharacter', isValid: checkUpperCaseCharacter(password), weight: 1 },
    { key: 'hasSpecialCharacter', isValid: checkSpecialCharacter(password), weight: 1 },
    { key: 'hasNoIllegalCharacter', isValid: checkIllegalCharacters(password), weight: 0.5 }, // Half weight
    { key: 'isDifferentFromEmail', isValid: checkPasswordIsDifferentFromEmail(password, email), weight: 0.5 }, // Half weight
  ];

  // Total weight for calculating exact percentage
  const totalWeight = results.reduce((acc, rule) => acc + rule.weight, 0);
  const validWeight = results.reduce((acc, rule) => acc + (rule.isValid ? rule.weight : 0), 0);

  // Calculate exact strength percentage
  const strengthPercentage = _.round((validWeight / totalWeight) * 100, 1);

  // Build the results object
  const resultsObj = results.reduce((acc, rule) => {
    acc[rule.key] = rule.isValid;
    return acc;
  }, {});

  return {
    ...resultsObj,
    isStrong: strengthPercentage === 100,
    strengthPercentage,
  };
}
